<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <b-card>
        <div slot="header">
          <h1>Planer - Werbekampagne bearbeiten</h1>
          <div class="card-header-actions"></div>
        </div>
        <b-row>
          <b-col lg="8">
            <b-form novalidate>
              <!-- === Title of Ad-Campaign === -->
              <b-form-group
                id="plannerInputGroup1"
                label="Titel der Werbekampagne *"
                label-for="plannertitle"
                description="(Der Titel Ihrer Werbekampagne muss zwischen 3 und 64 Zeichen lang sein)"
              >
                <b-form-input
                  :state="chkState('plannertitle')"
                  id="plannertitle"
                  type="text"
                  placeholder="Kampagnentitel"
                  @click="$v.form.plannertitle.$reset()"
                  v-model.trim="form.plannertitle"
                />
                <b-form-invalid-feedback :state="chkState('plannertitle')">
                  Pflichtfeld. Der Titel muss zwischen 3 und 64 Zeichen lang
                  sein.
                </b-form-invalid-feedback>
              </b-form-group>

              <!-- === Confirmed Advertisements === -->
              <b-form-group
                id="advertisementPlannerInputGroup1"
                label="Verfügbare aktive Werbungen"
                label-for="plannerAdvertisements"
              >
                <v-select
                  id="plannerAdvertisements"
                  ref="plannerAdvertisements"
                  :class="{
                    'error-border':
                      $v.form.plannerad.$dirty && $v.form.plannerad.$invalid,
                    'success-border':
                      $v.form.plannerad.$dirty && !$v.form.plannerad.$invalid,
                  }"
                  @search:focus="$v.form.plannerad.$reset()"
                  :state="chkState('plannerad')"
                  v-model="form.plannerad"
                  :options="getConfirmedAdvertisementsNames"
                  placeholder="Wählen Sie die gewünschte Werbungen aus"
                >
                  <span slot="no-options"
                    >Keine passenden Einträge gefunden</span
                  >
                </v-select>
                <b-form-invalid-feedback :state="chkState('plannerad')">
                  Pflichtfeld. Bitte wählen Sie mindestens 1 Werbung aus.
                </b-form-invalid-feedback>
              </b-form-group>

              <!-- === Available Publicitygroups === -->
              <b-form-group
                id="advertisementPlannerInputGroup2"
                label="Verfügbare Werbegruppen"
                label-for="plannerPublicitygroups"
              >
                <v-select
                  id="plannerPublicitygroups"
                  :class="{
                    'error-border':
                      $v.form.plannergroup.$dirty &&
                      $v.form.plannergroup.$invalid,
                    'success-border':
                      $v.form.plannergroup.$dirty &&
                      !$v.form.plannergroup.$invalid,
                  }"
                  ref="plannerPublicitygroups"
                  v-model="form.plannergroup"
                  @search:focus="$v.form.plannergroup.$reset()"
                  :options="getPublicitygroupNamesInclAll"
                  :state="chkState('plannergroup')"
                  placeholder="Wählen Sie die Zielgruppe aus"
                  ><span slot="no-options"
                    >Keine passenden Einträge gefunden</span
                  ></v-select
                >
                <b-form-invalid-feedback :state="chkState('plannergroup')">
                  Pflichtfeld. Bitte wählen Sie mindestens 1 Gruppe aus.
                </b-form-invalid-feedback>
              </b-form-group>

              <!-- === DatePicker === -->
              <b-form-group
                id="advertisementPlannerInputGroup3"
                label="Ausstrahlungs-Zeitraum der Werbung"
                label-for="plannerDatePicker"
              >
                <v-date-picker
                  mode="date"
                  v-model="form.plannerperiod"
                  :min-date="minDate"
                  :max-date="maxDate"
                  is-range
                  locale="de-at"
                />
                <!-- StartDatum -->
                <div class="planner-date-display">
                  gewähltes Startdatum:
                  {{ form.plannerperiod.start.toLocaleDateString("de-AT") }}
                </div>
                <!-- EndDatum -->
                <div class="planner-date-display">
                  gewähltes Enddatum:
                  {{ form.plannerperiod.end.toLocaleDateString("de-AT") }}
                </div>
                <!-- Dauer (in Tagen) -->
                <div class="planner-date-display">
                  Gesamtdauer (in Tagen):
                  {{
                    Math.round(
                      (form.plannerperiod.end - form.plannerperiod.start) /
                        (1000 * 60 * 60 * 24)
                    ) + 1
                  }}
                </div>
              </b-form-group>

              <!-- === Buttons === -->
              <b-button type="button" @click="onSubmit" variant="primary" :disabled="licenseExpired">
                Änderungen übernehmen
              </b-button>
              <b-button
                class="ml-1"
                type="button"
                @click="$router.go(-1)"
                variant="danger"
                >Änderungen verwerfen</b-button
              >
            </b-form>
          </b-col>
        </b-row>
      </b-card>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import vSelect from "vue-select";
import { DatePicker } from "v-calendar";
import { mapActions, mapGetters } from "vuex";

// VUE izitoast
import VueIziToast from "vue-izitoast";
import "izitoast/dist/css/iziToast.min.css";
Vue.use(VueIziToast);

import NotificationMixin from "../../mixins/notificationmixin";

import { validationMixin } from "vuelidate";
import { required, maxLength, minLength } from "vuelidate/lib/validators";

const tomorrow = new Date().setDate(new Date().getDate() + 1);

const formShape = {
  plannerid: "",
  plannertitle: "",
  plannerad: "",
  plannergroup: "",
  plannerperiod: {
    start: new Date(tomorrow),
    end: new Date(tomorrow),
  },
};
export default {
  name: "Advertisement-Planner",
  computed: {
    ...mapGetters([
      "getAdvertisementStatus",
      "getConfirmedAdvertisementsNames",
      "getPublicitygroupStatus",
      "getPublicitygroupNamesInclAll",
      "getPlanOverview",
      "getPlanStatus",
      "getLicenseExpiration",
      "licenseExpired"
    ]),
    isValid() {
      return !this.$v.form.$invalid;
    },
    calcMaxDate() {
      return this.getLicenseExpiration ? new Date(this.getLicenseExpiration) : new Date()
    },
    toAustrianDate(d){
      console.log(d)
      if(d){
        const dateParts = d.split("/")
      const day = dateParts[1]
      const month = dateParts[0]
      const year = dateParts[2]
      return `${day}.${month}.${year}`
      }
      return ``
    }
  },
  methods: {
    ...mapActions([
      "loadPublicitygroups",
      "loadAdvertisements",
      "loadPlans",
      "updatePlan",
    ]),
    async onSubmit(e) {
      e.preventDefault();
      // force validations at this point
      this.$v.$touch();

      // if already submitted, prevent from re-submitting;
      // if one wants to submit another ad, first click 'dismiss'
      if (this.submitted === true) {
        this.notify(
          1,
          ``,
          `Die Änderungen wurden bereits abgeschickt.
          Bitte wählen Sie erst "Verwerfen" um eine andere/weitere Werbekampagne zu bearbeiten.`,
          this.$toast
        );
        return;
      }
      // only accept submission, once all fields are valid
      if (this.isValid) {
        let err = await this.updatePlan(this.form);
        this.notify(
          err,
          `Die Werbekampagne wurde erfolgreich geändert!`,
          `Die Werbekampagne konnte aufgrund einer Terminkollision nicht geändert werden!`,
          this.$toast
        );
        // if submission is successful, set submitted flag
        if (!err) {
          this.submitted = true;
          setTimeout(() => {
            this.$router.go(-1);
          }, 1500);
        }
      }
    },
    chkState(val) {
      const field = this.$v.form[val];
      if (!field.$dirty) {
        return null;
      }
      return !(field.$error || field.$model === "");
    },
    setFields(plan) {
      this.form.plannerid = plan.plannerid;
      this.form.plannertitle = plan.plannertitle;
      this.form.plannerad = plan.planneradvertisement;
      this.form.plannergroup = plan.plannergroup;
      this.form.plannerperiod.start = new Date(plan.plannerstartdate);
      this.form.plannerperiod.end = new Date(plan.plannerenddate);
    },
  },
  mounted: function () {
    if (!this.getAdvertisementStatus) {
      this.loadAdvertisements();
    }

    if (!this.getPublicitygroupStatus) {
      this.loadPublicitygroups();
    }

    if (!this.getPlanStatus) {
      this.loadPlans();
    }

    // set fields
    this.setFields(this.$route.params.plan);
  },
  mixins: [NotificationMixin, validationMixin],
  data() {
    return {
      form: Object.assign({}, formShape),
      today: new Date(),
      minDate: new Date(tomorrow),
      maxDate: this.calcMaxDate,
      theme: "bootstrap4",
      template: "default",
      feedBack: "secondary",
      submitted: false,
      plannerid: "",
    };
  },
  validations: {
    form: {
      plannertitle: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(64),
      },
      plannerad: {
        required,
      },
      plannergroup: {
        required,
      },
    },
  },
  components: {
    vSelect,
    "v-date-picker": DatePicker,
  },
};
</script>
